import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

function GovernmentDefense() {
  return (
    <div className="pt-28">
      {/* Hero Section with Background Image */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1708733818543-8a895c354151?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
        ></div>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 text-gold mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Government & <span className="text-gold">Defense</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Specialized construction and support services for federal agencies, DoD installations, and military bases
          </p>
        </div>
      </section>

      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
            <p>
              Williams Diversified brings specialized expertise to government and defense sector projects. We understand the unique requirements of federal facilities, military installations, and secure environments where compliance, speed, and reliability are non-negotiable.
            </p>
            <p>
              Our teams are experienced in working within secure environments, following strict protocols, and maintaining the operational readiness that government and defense clients demand. From rapid emergency response to long-term base operations support, we deliver mission-critical services with precision and accountability.
            </p>
            
            <h3 className="text-3xl font-bold text-white mt-12 mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Our Government & Defense Capabilities</h3>
            <ul className="space-y-4">
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Base operations and DoD facility support</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Rapid deployment and emergency response</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Construction of secure federal facilities</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Military housing and temporary camp setup</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Defense and aerospace facility construction</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Federal facility maintenance and renovations</span></li>
            </ul>

            <div className="bg-black border border-gold/30 rounded-lg p-8 mt-12">
              <h4 className="text-2xl font-bold text-gold mb-4">Compliance & Readiness</h4>
              <p className="text-gray-300 leading-relaxed">
                Our team maintains deep knowledge of federal acquisition regulations and compliance requirements for government and defense projects. We're prepared to work seamlessly with contracting officers, base commanders, and federal program managers to deliver on-time, on-spec results that meet strict government standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Facility Maintenance Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-8 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Government & Defense <span className="text-gold">Facility Maintenance</span>
          </h3>
          <p className="text-gray-300 text-lg leading-relaxed text-center mb-8">
            Mission-critical facility maintenance services designed for federal agencies, military installations, and defense contractors with strict compliance standards.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6">
              <h4 className="text-gold font-bold text-xl mb-3">Base Operations Support</h4>
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Facility maintenance & repair</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Grounds maintenance & landscaping</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>HVAC & mechanical systems</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Security system maintenance</li>
              </ul>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6">
              <h4 className="text-gold font-bold text-xl mb-3">Rapid Response Services</h4>
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start"><span className="text-gold mr-2">•</span>48-hour emergency deployment</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Disaster recovery support</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Critical infrastructure repairs</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Temporary facility setup</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* National Rollout Programs Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-4 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            National <span className="text-gold">Rollout Programs</span>
          </h3>
          <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">
            Scalable construction solutions for government agencies requiring consistent quality and compliance across multiple facilities
          </p>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">50+</div>
              <div className="text-gray-300 font-medium mb-2">States Covered</div>
              <p className="text-gray-500 text-sm">Nationwide service capability with local expertise</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">1</div>
              <div className="text-gray-300 font-medium mb-2">Point of Contact</div>
              <p className="text-gray-500 text-sm">Single account manager for all locations</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">100%</div>
              <div className="text-gray-300 font-medium mb-2">Compliance</div>
              <p className="text-gray-500 text-sm">Full FAR/DFAR compliance on every project</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">24/7</div>
              <div className="text-gray-300 font-medium mb-2">Support</div>
              <p className="text-gray-500 text-sm">Round-the-clock emergency response</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-4 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Why Choose Us for <span className="text-gold">Government & Defense</span>
          </h3>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Mission-critical expertise for federal agencies and military installations
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">01</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Security Clearance Ready</h4>
                <p className="text-gray-400 text-sm mb-4">Experienced working in classified environments with cleared personnel. Full compliance with federal security protocols and access requirements.</p>
              </div>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">02</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Rapid Deployment</h4>
                <p className="text-gray-400 text-sm mb-4">48-hour emergency response capability with pre-positioned equipment and trained crews ready for immediate mobilization nationwide.</p>
              </div>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">03</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">FAR/DFAR Compliant</h4>
                <p className="text-gray-400 text-sm mb-4">Deep expertise in Federal Acquisition Regulations and defense contracting requirements. Seamless integration with government procurement processes.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-12 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Our Government <span className="text-gold">Contracting Process</span>
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">1</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Security Clearance</h4>
              <p className="text-gray-400 text-sm">Personnel vetting and clearance verification to meet facility access requirements.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">2</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Compliance Planning</h4>
              <p className="text-gray-400 text-sm">FAR/DFAR review and documentation preparation for full regulatory compliance.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">3</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Mission Execution</h4>
              <p className="text-gray-400 text-sm">Rapid mobilization and construction with strict adherence to government protocols.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">4</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Final Inspection</h4>
              <p className="text-gray-400 text-sm">Comprehensive closeout with complete documentation for contracting officer approval.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Mission-Ready <span className="text-gold">Support</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            Partner with a contractor who understands government and defense requirements
          </p>
          <Link to="/contact" className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider">Contact Us</Link>
        </div>
      </section>
    </div>
  );
}

export default GovernmentDefense;